import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, FileText, Image, Video, Archive, AlertCircle } from 'lucide-react';
import Button from './Button';
import toast from 'react-hot-toast';
import { taskAttachmentService } from '@/services/taskAttachmentService';
import { deliveryAttachmentService } from '@/services/deliveryAttachmentService';
import { subTaskAttachmentService } from '@/services/subTaskAttachmentService';

interface FilePickerProps {
    files?: File[];
    onFilesChange?: (files: File[]) => void;
    maxFiles?: number;
    maxFileSize?: number;
    disabled?: boolean;
    className?: string;

    taskId?: number;
    subTaskId?: number;
    deliveryId?: number;
    onUploadSuccess?: (attachments: any[]) => void;
    showUploadButton?: boolean;
}

interface FileWithPreview {
    file: File;
    id: string;
    error?: string;
}

const FilePicker: React.FC<FilePickerProps> = ({
    files = [],
    onFilesChange,
    maxFiles = 10,
    maxFileSize = 10,
    disabled = false,
    className = '',
    taskId,
    subTaskId,
    deliveryId,
    onUploadSuccess,
    showUploadButton = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const allowedTypes = [

        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv',
        'application/json',
        'text/json',

        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',

        'video/mp4',
        'video/webm',
        'video/ogg',
        'video/avi',
        'video/quicktime',
        'video/x-msvideo',

        'audio/mp3',
        'audio/mpeg',
        'audio/wav',
        'audio/ogg',

        'application/zip',
        'application/x-rar-compressed',
        'application/x-7z-compressed',

        'application/octet-stream'
    ];

    const validateFile = useCallback((file: File): string | null => {
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.split('.').pop();
        

        const allowedExtensions = ['json', 'ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx', 'pdf', 'txt', 'csv', 'sql', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'mp4', 'avi', 'mp3', 'wav', 'zip', 'rar', '7z'];

        if (fileExtension && allowedExtensions.includes(fileExtension)) {

        } else {
            return `Extensão não permitida: .${fileExtension}`;
        }
        
        const maxSizeBytes = maxFileSize * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            return `Arquivo muito grande. Tamanho máximo: ${maxFileSize}MB`;
        }
        
        return null;
    }, [maxFileSize]);

    const addFiles = useCallback((newFiles: File[]) => {
        const currentCount = files.length;
        const availableSlots = maxFiles - currentCount;
        
        if (newFiles.length > availableSlots) {
            toast.error(`Máximo de ${maxFiles} arquivos permitidos. Você pode adicionar apenas mais ${availableSlots} arquivo(s).`);
            newFiles = newFiles.slice(0, availableSlots);
        }

        const validFiles: File[] = [];
        const errors: string[] = [];

        newFiles.forEach(file => {

            const exists = files.some(existingFile => 
                existingFile.name === file.name && existingFile.size === file.size
            );
            
            if (exists) {
                errors.push(`"${file.name}" já foi selecionado`);
                return;
            }

            const error = validateFile(file);
            if (error) {
                errors.push(`"${file.name}": ${error}`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            toast.error(errors.join('\n'));
        }

        if (validFiles.length > 0) {
            const updatedFiles = [...files, ...validFiles];
            onFilesChange?.(updatedFiles);
            toast.success(`${validFiles.length} arquivo(s) selecionado(s)`);
        }
    }, [files, maxFiles, validateFile, onFilesChange]);

    const removeFile = useCallback((fileToRemove: File) => {
        const updatedFiles = files.filter(file => file !== fileToRemove);
        onFilesChange?.(updatedFiles);
    }, [files, onFilesChange]);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);

        if (disabled) return;

        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    }, [addFiles, disabled]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            addFiles(selectedFiles);
            e.target.value = '';
        }
    };

    const getFileIcon = (contentType: string) => {
        if (contentType.startsWith('image/')) {
            return <Image className="w-4 h-4 text-green-500" />;
        }
        if (contentType.startsWith('video/')) {
            return <Video className="w-4 h-4 text-red-500" />;
        }
        if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('text')) {
            return <FileText className="w-4 h-4 text-blue-500" />;
        }
        if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('archive')) {
            return <Archive className="w-4 h-4 text-yellow-500" />;
        }
        return <File className="w-4 h-4 text-text-tertiary" />;
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUpload = async () => {
        if ((!taskId && !subTaskId && !deliveryId) || !files.length || uploading) return;

        setUploading(true);
        try {
            let attachments;

            if (subTaskId) {
                attachments = await subTaskAttachmentService.uploadFiles(subTaskId, files);
            } else if (taskId) {
                attachments = await taskAttachmentService.uploadFiles(taskId, files);
            } else if (deliveryId) {
                attachments = await deliveryAttachmentService.uploadFiles(deliveryId, files);
            }

            toast.success(`${files.length} arquivo(s) enviado(s) com sucesso!`);
            onUploadSuccess?.(attachments);
            onFilesChange?.([]);
        } catch (error) {
            console.error('Error uploading files:', error);
            toast.error('Erro ao enviar arquivos');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={className}>
            {/* Drop Zone */}
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragOver
                        ? 'border-blue-400 bg-info-soft'
                        : disabled
                        ? 'border-border-subtle bg-surface-app'
                        : 'border-border-strong hover:border-text-tertiary'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled) setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
            >
                <Upload className={`mx-auto h-12 w-12 ${isDragOver ? 'text-blue-400' : 'text-text-tertiary'}`} />
                <div className="mt-4">
                    <p className="text-sm font-medium text-text-primary">
                        {disabled 
                            ? 'Upload desabilitado'
                            : isDragOver 
                            ? 'Solte os arquivos aqui'
                            : 'Arraste arquivos aqui ou'
                        }
                    </p>
                    {!disabled && (
                        <>
                            <Button
                                type="button"
                                variant="ghost"
                                className="mt-2"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Selecionar arquivos
                            </Button>
                            <p className="text-xs text-text-tertiary mt-2">
                                Máximo {maxFiles} arquivos, {maxFileSize}MB cada
                            </p>
                        </>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={[...allowedTypes, '.sql'].join(',')}
                    onChange={handleFileSelect}
                    disabled={disabled}
                />
            </div>

            {/* Selected Files List */}
            {files.length > 0 && (
                <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-text-primary">
                            Arquivos selecionados ({files.length})
                        </h4>
                        <div className="flex items-center space-x-2">
                            {/* Botão de Upload (apenas na edição) */}
                            {showUploadButton && files.length > 0 && (taskId || subTaskId || deliveryId) && !disabled && (
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    className="text-xs"
                                >
                                    {uploading ? 'Enviando...' : `Enviar ${files.length} arquivo(s)`}
                                </Button>
                            )}
                            {!disabled && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onFilesChange?.([])}
                                    className="text-xs text-text-tertiary hover:text-red-600"
                                >
                                    Limpar todos
                                </Button>
                            )}
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        {files.map((file, index) => (
                            <div key={`${file.name}-${index}`} className="flex items-center justify-between p-3 bg-surface-app rounded-lg">
                                <div className="flex items-center flex-1 min-w-0">
                                    <div className="flex-shrink-0">
                                        {getFileIcon(file.type)}
                                    </div>
                                    <div className="ml-3 flex-1 min-w-0">
                                        <p className="text-sm font-medium text-text-primary truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-text-tertiary">
                                            {formatFileSize(file.size)}
                                        </p>
                                    </div>
                                </div>
                                
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => removeFile(file)}
                                        className="ml-3 p-1 hover:bg-red-100 hover:text-red-600 rounded"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilePicker;